import exifr from 'exifr';

export interface GPSCoordinates {
  latitude: number;
  longitude: number;
}

export interface ExifData {
  gps?: GPSCoordinates;
  dateTaken?: Date;
  make?: string;
  model?: string;
}

/**
 * Extract GPS coordinates and other metadata from an image file
 * Returns null for GPS if no location data is found (privacy settings, edited photos, etc.)
 * Note: ~30% of photos lack GPS data
 */
export async function extractExifData(file: File): Promise<ExifData> {
  try {
    // Only process images, not videos
    if (!file.type.startsWith('image/')) {
      return {};
    }

    const exif = await exifr.parse(file, {
      gps: true,
      pick: ['GPSLatitude', 'GPSLongitude', 'DateTimeOriginal', 'Make', 'Model']
    });

    if (!exif) {
      return {};
    }

    const result: ExifData = {};

    // Extract GPS coordinates
    if (exif.latitude !== undefined && exif.longitude !== undefined) {
      result.gps = {
        latitude: exif.latitude,
        longitude: exif.longitude
      };
    }

    // Extract date taken
    if (exif.DateTimeOriginal) {
      result.dateTaken = new Date(exif.DateTimeOriginal);
    }

    // Extract device info
    if (exif.Make) result.make = exif.Make;
    if (exif.Model) result.model = exif.Model;

    return result;
  } catch (error) {
    console.error('[exifUtils] Error extracting EXIF data:', error);
    return {};
  }
}

/**
 * Check if a file has GPS coordinates
 */
export async function hasGPSData(file: File): Promise<boolean> {
  const exif = await extractExifData(file);
  return exif.gps !== undefined;
}
